import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import StatusTimeline from "@/components/StatusTimeline";
import ProjectPhotoTimeline from "@/components/ProjectPhotoTimeline";
import { DailyLogTimeline } from "@/components/DailyLog";
import ProductionDashboard from "@/components/ProductionDashboard";
import { Camera, ListChecks, FileText, Zap, MessageSquare } from "lucide-react";
import ProjectChatPanel from "@/components/ProjectChatPanel";

const STATUS_COLOR = {
  ordered: "bg-slate-200 text-slate-700",
  in_process: "bg-blue-100 text-blue-700",
  awaiting_arrival: "bg-amber-100 text-amber-700",
  scheduling: "bg-purple-100 text-purple-700",
  scheduled: "bg-indigo-100 text-indigo-700",
  installed: "bg-emerald-100 text-emerald-700",
  completed: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-red-100 text-red-700",
};

export default function MyProjects() {
  const [projects, setProjects] = useState([]);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("timeline"); // timeline | photos | log | production | messages
  const [unreadByProject, setUnreadByProject] = useState({}); // {project_id: count}

  useEffect(() => {
    (async () => {
      const r = await api.get("/projects/my");
      setProjects(r.data);
      // Pre-select project from ?msg=<pid> if present, else first project.
      const params = new URLSearchParams(window.location.search);
      const preselect = params.get("msg");
      const found = preselect ? r.data.find((p) => p.project_id === preselect) : null;
      const initial = found || r.data[0] || null;
      if (initial) {
        setSelected(initial);
        if (found) setTab("messages");
      }
      // Fetch unread counts.
      try {
        const rooms = await api.get("/client/project-message-rooms");
        const m = {};
        (rooms.data || []).forEach((r) => { m[r.project_id] = r.unread_count || 0; });
        setUnreadByProject(m);
      } catch (err) {
        // Non-critical — unread badges silently degrade to zero if the endpoint
        // fails. Log so operators can spot API drift without breaking the tab.
        console.warn("unread counts fetch failed", err);
      }
    })();
  }, []);

  // When switching to messages tab, optimistically clear the badge.
  useEffect(() => {
    if (tab === "messages" && selected) {
      setUnreadByProject((prev) => ({ ...prev, [selected.project_id]: 0 }));
    }
  }, [tab, selected?.project_id]); // eslint-disable-line

  return (
    <div data-testid="my-projects">
      <p className="overline">Track every step</p>
      <h1 className="text-3xl font-heading font-medium text-[#0A2540] mt-2 mb-8">My Projects</h1>

      {projects.length === 0 ? (
        <div className="card-pb p-12 text-center">
          <p className="text-[#475569]">No projects yet. Request a quote to get started.</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-6 items-start">
          <div className="space-y-3 lg:max-h-[calc(100vh-9rem)] lg:overflow-y-auto lg:pr-2">
            {projects.map((p) => (
              <button
                key={p.project_id}
                data-testid={`project-card-${p.project_id}`}
                onClick={() => setSelected(p)}
                className={`w-full text-left card-pb p-5 transition-all relative ${
                  selected?.project_id === p.project_id ? "border-[#C9A356] border-2" : "hover:-translate-y-0.5"
                }`}
              >
                {unreadByProject[p.project_id] > 0 && (
                  <span
                    className="absolute -top-2 -right-2 min-w-[22px] h-[22px] px-1.5 grid place-items-center rounded-full bg-red-500 text-white text-[11px] font-bold shadow-md"
                    data-testid={`unread-badge-${p.project_id}`}
                  >
                    {unreadByProject[p.project_id]}
                  </span>
                )}
                <div className="flex justify-between items-start mb-2">
                  <div className="font-heading font-medium text-[#0A2540]">{p.title}</div>
                  <span className={`text-xs font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${STATUS_COLOR[p.status]}`}>
                    {p.status?.replace(/_/g, " ")}
                  </span>
                </div>
                <div className="text-xs text-[#475569]">{p.project_type}</div>
                <div className="w-full bg-slate-100 rounded-full h-1.5 mt-3">
                  <div className="bg-[#C9A356] h-1.5 rounded-full" style={{ width: `${p.progress_pct}%` }}></div>
                </div>
              </button>
            ))}
          </div>
          <div className="lg:col-span-2 card-pb p-6 lg:sticky lg:top-6">
            {selected ? (
              <>
                <div className="mb-6">
                  <div className="overline mb-2">{selected.customer_code}</div>
                  <h2 className="font-heading text-2xl text-[#0A2540] dark:text-white">{selected.title}</h2>
                  <p className="text-[#475569] dark:text-slate-400 mt-1">{selected.notes || "—"}</p>
                  {selected.install_date && (
                    <div className="mt-3 inline-block px-3 py-1 bg-[#F3E8CE] text-[#0A2540] text-sm rounded-md">
                      Install: {selected.install_date}
                    </div>
                  )}
                </div>

                {/* Tabs */}
                <div className="flex gap-1 border-b border-[#E2E8F0] dark:border-slate-700 mb-5">
                  <button onClick={() => setTab("timeline")} data-testid="tab-timeline"
                          className={`px-4 py-2 text-sm font-medium border-b-2 transition ${tab === "timeline" ? "border-[#C9A356] text-[#0A2540] dark:text-white" : "border-transparent text-[#475569] dark:text-slate-400 hover:text-[#0A2540] dark:hover:text-white"}`}>
                    <ListChecks className="w-4 h-4 inline mr-1.5" /> Status timeline
                  </button>
                  <button onClick={() => setTab("photos")} data-testid="tab-photos"
                          className={`px-4 py-2 text-sm font-medium border-b-2 transition ${tab === "photos" ? "border-[#C9A356] text-[#0A2540] dark:text-white" : "border-transparent text-[#475569] dark:text-slate-400 hover:text-[#0A2540] dark:hover:text-white"}`}>
                    <Camera className="w-4 h-4 inline mr-1.5" /> Photo timeline
                  </button>
                  <button onClick={() => setTab("log")} data-testid="tab-log"
                          className={`px-4 py-2 text-sm font-medium border-b-2 transition ${tab === "log" ? "border-[#C9A356] text-[#0A2540] dark:text-white" : "border-transparent text-[#475569] dark:text-slate-400 hover:text-[#0A2540] dark:hover:text-white"}`}>
                    <FileText className="w-4 h-4 inline mr-1.5" /> Field log
                  </button>
                  {["installed", "completed"].includes(selected.status) && (
                    <button onClick={() => setTab("production")} data-testid="tab-production"
                            className={`px-4 py-2 text-sm font-medium border-b-2 transition ${tab === "production" ? "border-[#C9A356] text-[#0A2540] dark:text-white" : "border-transparent text-[#475569] dark:text-slate-400 hover:text-[#0A2540] dark:hover:text-white"}`}>
                      <Zap className="w-4 h-4 inline mr-1.5" /> Live production
                    </button>
                  )}
                  <button onClick={() => setTab("messages")} data-testid="tab-messages"
                          className={`relative px-4 py-2 text-sm font-medium border-b-2 transition ${tab === "messages" ? "border-[#C9A356] text-[#0A2540] dark:text-white" : "border-transparent text-[#475569] dark:text-slate-400 hover:text-[#0A2540] dark:hover:text-white"}`}>
                    <MessageSquare className="w-4 h-4 inline mr-1.5" /> Messages
                    {unreadByProject[selected.project_id] > 0 && tab !== "messages" && (
                      <span
                        className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold"
                        data-testid={`tab-messages-badge`}
                      >
                        {unreadByProject[selected.project_id]}
                      </span>
                    )}
                  </button>
                </div>

                {tab === "timeline" && (
                  <>
                    <h3 className="font-heading text-lg text-[#0A2540] dark:text-white mb-4">Status Timeline</h3>
                    <StatusTimeline status={selected.status} timeline={selected.timeline} />
                  </>
                )}
                {tab === "photos" && (
                  <ProjectPhotoTimeline projectId={selected.project_id} canEdit={false} projectTitle={selected.title} />
                )}
                {tab === "log" && (
                  <>
                    <h3 className="font-heading text-lg text-[#0A2540] dark:text-white mb-4">Daily Field Log</h3>
                    <DailyLogTimeline projectId={selected.project_id} />
                  </>
                )}
                {tab === "production" && (
                  <ProductionDashboard projectId={selected.project_id} />
                )}
                {tab === "messages" && (
                  <ProjectChatPanel
                    projectId={selected.project_id}
                    title={`Messages · ${selected.title}`}
                    subtitle="Direct line to your PowerBuilt team"
                    height="h-[60vh]"
                  />
                )}
              </>
            ) : (
              <p className="text-[#475569] dark:text-slate-400">Select a project to view details</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
